import type { QuoteCloseReason, QuoteStatus } from './schemas/quotes'

/**
 * 견적 요청·제안 상태 (명세 2026-10-30 P0-02 '상태 분리'). 이름은 명세 그대로다.
 *
 * 저장은 두 칸이다. quote_requests.status 는 예전 네 값(submitted·quoted·accepted·closed)을 그대로 지킨다 — 이미 배포된
 * 1.0.3~1.0.7 앱과 서버 곳곳의 조건이 그 값을 본다. 명세 상태는 quote_requests.request_status 에 함께 적고,
 * 읽을 때는 quoteRequestStatusOf 가 두 칸이 맞는지 확인해 고른다(맞지 않으면 예전 값에서 끌어낸다).
 * 상태를 바꾸는 곳(관리자 PATCH·제안 등록/삭제·고객 취소·계정 삭제)은 모두 canTransitionQuoteRequest 하나로 허용을 본다.
 */

/** 견적 요청 상태. draft 는 명세에 있지만 지금은 저장 전 앱 화면에만 있다(서버에 쓰이지 않는다) */
export const QUOTE_REQUEST_STATUSES = [
  'draft',
  'submitted',
  'needs_information',
  'reviewing',
  'offered',
  'closed',
  'cancelled',
] as const
export type QuoteRequestStatus = (typeof QUOTE_REQUEST_STATUSES)[number]

/**
 * 공장 제안 상태. 지금 서버가 정하는 것은 issued·accepted·declined·expired 넷이다(quoteOfferStatusOf).
 * draft(공개 전 초안)·superseded(가격·기한이 바뀌어 새 제안으로 대체)는 명세 이름으로 자리만 둔다 —
 * 제안 수정은 아직 같은 행을 고치고 이전 값은 quote_offer_events 에 남는다
 */
export const QUOTE_OFFER_STATUSES = [
  'draft',
  'issued',
  'accepted',
  'declined',
  'expired',
  'superseded',
] as const
export type QuoteOfferStatus = (typeof QUOTE_OFFER_STATUSES)[number]

/**
 * 상태를 바꾸는 쪽. customer = 앱, admin = 관리자 화면, factory = 공장 쪽 작업공간(제조 연동),
 * system = 다른 일의 결과로 따라 바뀜(제안 등록·삭제, 계정 삭제)
 */
export const QUOTE_STATUS_ACTORS = ['customer', 'admin', 'factory', 'system'] as const
export type QuoteStatusActor = (typeof QUOTE_STATUS_ACTORS)[number]

type Transitions = Readonly<
  Record<QuoteRequestStatus, Partial<Record<QuoteRequestStatus, readonly QuoteStatusActor[]>>>
>

/**
 * 허용 전이 표 — 단일 원본. 표에 없는 전이는 누구도 못 한다. 같은 상태로의 전이도 표 밖이다(알림이 두 번 가지 않게).
 * - 고객 취소는 제안이 오기 전(submitted·reviewing·needs_information)에만 (C48). 제안 0건 조건은 quoteCancellable 이 더 본다
 * - offered 는 제안이 생기면 시스템이 옮긴다. 마지막 제안을 지우면 reviewing 으로 돌아간다
 * - 마감 다시 열기(2026-10-24 대표 결정)는 관리자만, 이유가 있어야 한다(관리자 경로가 확인). 고객 취소(cancelled)는 다시 열지 않는다
 */
const QUOTE_REQUEST_TRANSITIONS: Transitions = {
  draft: { submitted: ['customer'], cancelled: ['customer'] },
  submitted: {
    reviewing: ['admin', 'factory'],
    needs_information: ['admin', 'factory'],
    offered: ['system'],
    closed: ['admin', 'system'],
    cancelled: ['customer'],
  },
  reviewing: {
    needs_information: ['admin', 'factory'],
    offered: ['system'],
    closed: ['admin', 'system'],
    cancelled: ['customer'],
  },
  needs_information: {
    reviewing: ['admin', 'factory'],
    offered: ['system'],
    closed: ['admin', 'system'],
    cancelled: ['customer'],
  },
  offered: { reviewing: ['system'], closed: ['admin', 'system'] },
  closed: { submitted: ['admin'], offered: ['admin'] },
  cancelled: {},
}

export function canTransitionQuoteRequest(
  from: QuoteRequestStatus,
  to: QuoteRequestStatus,
  actor: QuoteStatusActor,
): boolean {
  return QUOTE_REQUEST_TRANSITIONS[from][to]?.includes(actor) ?? false
}

/** 이 상태에서 이 쪽이 갈 수 있는 상태들 (관리자 화면의 버튼) */
export function nextQuoteRequestStatuses(
  from: QuoteRequestStatus,
  actor: QuoteStatusActor,
): QuoteRequestStatus[] {
  return QUOTE_REQUEST_STATUSES.filter((to) => canTransitionQuoteRequest(from, to, actor))
}

/**
 * 명세 상태 → 예전 status 칸 값. 옛 앱·옛 조건은 이 값만 본다.
 * 공장 확인·추가 정보 요청은 예전에는 '접수'였고, 제안 도착은 고객이 고르기 전 quoted·고른 뒤 accepted 다
 */
export function legacyQuoteStatus(
  status: QuoteRequestStatus,
  selectedOfferId: string | null,
): QuoteStatus {
  switch (status) {
    case 'draft':
    case 'submitted':
    case 'reviewing':
    case 'needs_information':
      return 'submitted'
    case 'offered':
      return selectedOfferId ? 'accepted' : 'quoted'
    case 'closed':
    case 'cancelled':
      return 'closed'
  }
}

export interface QuoteStatusSource {
  status: QuoteStatus
  /** request_status 칸. 이 칸이 생기기 전 행·옛 서버 응답에는 없다 */
  requestStatus?: QuoteRequestStatus | null
  closeReason?: QuoteCloseReason | null
  selectedOfferId: string | null
}

/**
 * 지금 명세 상태. 저장된 request_status 가 예전 칸과 맞을 때만 그 값을 쓰고, 아니면 예전 칸에서 끌어낸다 —
 * 예전 칸만 고치는 경로(옛 서버, 제조 연동 동기화)가 지나가도 엇갈린 값을 보이지 않는다
 */
export function quoteRequestStatusOf(quote: QuoteStatusSource): QuoteRequestStatus {
  const stored = quote.requestStatus
  if (stored && legacyQuoteStatus(stored, quote.selectedOfferId) === quote.status) return stored
  switch (quote.status) {
    case 'submitted':
      return 'submitted'
    case 'quoted':
    case 'accepted':
      return 'offered'
    case 'closed':
      return quote.closeReason === 'user_cancelled' ? 'cancelled' : 'closed'
  }
}

/**
 * 제안 유효 기한을 비교할 '오늘' (YYYY-MM-DD). 공장·고객 시간대를 모르므로 가장 늦은 시간대(UTC-12)의 날짜 —
 * 그날이 어디서나 지난 뒤에만 만료로 본다. 서버의 선택 확인(routes/quotes.ts)과 같은 값을 쓴다
 */
export function quoteOfferToday(now: Date): string {
  return new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** 제안 하나의 상태. 고객이 고른 제안은 accepted, 다른 제안을 골랐으면 declined, 기한이 지났으면 expired */
export function quoteOfferStatusOf(
  offer: { id: string; validUntil: string | null },
  quote: { selectedOfferId: string | null },
  today: string,
): QuoteOfferStatus {
  if (quote.selectedOfferId === offer.id) return 'accepted'
  if (quote.selectedOfferId) return 'declined'
  if (offer.validUntil && DATE_RE.test(offer.validUntil) && offer.validUntil < today)
    return 'expired'
  return 'issued'
}
